import { useState, useEffect, useContext, useMemo, useRef, type ChangeEvent, type FocusEvent, type ReactElement, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthProvider";
import PageTemplate from "../components/PageTemplate";
import SlidePageTransition from "../components/transitions/SlidePageTransition";
import { useTranslation } from "react-i18next";
import EasterTimingIndicator from "../components/EasterTimingIndicator";
import ErrorPage from "../components/ErrorPage";
import Spinner from "../components/Spinner";
import {
  FaArrowRight,
  FaBirthdayCake,
  FaCalendarAlt,
  FaChild,
  FaExclamationTriangle,
  FaFilter,
  FaGenderless,
  FaMars,
  FaPlus,
  FaSearch,
  FaTimes,
  FaVenus,
  FaUsers,
} from "react-icons/fa";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import CommittedValidate from "../components/CommitedValidate";
import WaitingValidate from "../components/WaitingValidate";
import { parseToDate } from "../utils/dateUtils";
import { useCan } from "../authz/useCan";
import {
  getCachedAlumniChildren,
  getCachedChildren,
  getChildrenUiState,
  hasAlumniChildrenCache,
  hasChildrenCache,
  loadAlumniChildrenOverview,
  loadChildrenOverview,
  setChildrenUiState,
} from "./familyOverviewCache";


type Identifier = string | number;
type BadgeTone = "success" | "warn" | "danger" | "neutral";

interface ChildRecord {
  id: Identifier;
  firstName?: string;
  lastName?: string;
  gender?: string;
  committed?: boolean;
  waiting?: boolean;
  dateRegistration?: string | Date | null;
  dateBirth?: string | Date | null;
  withdrawnAt?: unknown;
  deceasedAt?: unknown;
  deletionRequestedAt?: unknown;
  status?: string;
  familyCount?: number;
  families?: unknown[];
  familyIds?: Identifier[];
  familiesIds?: Identifier[];
}

interface MappedError {
  message?: string;
  status?: number;
}

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
}

const initialsForChild = (child: ChildRecord): string =>
  [child?.firstName, child?.lastName]
    .filter(Boolean)
    .map((part) => String(part).trim().charAt(0).toUpperCase())
    .join("")
    .slice(0, 2) || "?";

export const getChildFamilyCount = (child: ChildRecord): number => {
  if (typeof child?.familyCount === "number") return child.familyCount;
  if (Array.isArray(child?.families)) return child.families.length;
  if (Array.isArray(child?.familyIds)) return child.familyIds.length;
  if (Array.isArray(child?.familiesIds)) return child.familiesIds.length;
  return 0;
};

const Badge = ({ children, tone = "neutral" }: BadgeProps): ReactElement => {
  const tones: Record<BadgeTone, string> = {
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200",
    warn:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200",
    danger:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200",
    neutral:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${tones[tone]}`}>
      {children}
    </span>
  );
};

const ChildrenOverview = (): ReactElement => {
  const { keycloak } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cachedUiState = useMemo(() => getChildrenUiState(), []);
  const cachedChildren = useMemo(() => getCachedChildren(), []);
  const cachedAlumni = useMemo(() => getCachedAlumniChildren(), []);
  const canManageFamiliesLegacy = useCan("families", "crud");
  const canManageFamiliesDirect = useCan("families", "crud");
  const canManageFamiliesManage = useCan("families", "manage");
  const canManageFamilies = canManageFamiliesLegacy || canManageFamiliesDirect || canManageFamiliesManage;
  const canCreateChildrenCrud = useCan("family.children", "crud");
  const canCreateChildrenManage = useCan("family.children", "manage");
  const canCreateChildren = canCreateChildrenCrud || canCreateChildrenManage;

  const [children, setChildren] = useState<ChildRecord[]>(() => (cachedChildren ?? []) as ChildRecord[]);
  const [alumni, setAlumni] = useState<ChildRecord[]>(() => (cachedAlumni ?? []) as ChildRecord[]);
  const [loading, setLoading] = useState(() => !hasChildrenCache());
  const [alumniLoading, setAlumniLoading] = useState(() => cachedUiState.showAlumni && !hasAlumniChildrenCache());
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState<MappedError | null>(null);
  const [alumniError, setAlumniError] = useState<MappedError | null>(null);
  const [search, setSearch] = useState(cachedUiState.search);
  const [onlyMultiFamilies, setOnlyMultiFamilies] = useState(cachedUiState.onlyMultiFamilies);
  const [onlyWithoutFamily, setOnlyWithoutFamily] = useState(Boolean(cachedUiState.onlyWithoutFamily));
  const [showAlumni, setShowAlumni] = useState(cachedUiState.showAlumni);
  const [showDeletionRequested, setShowDeletionRequested] = useState(Boolean(cachedUiState.showDeletionRequested));
  const [filterOpen, setFilterOpen] = useState(false);
  const [alumniFetched, setAlumniFetched] = useState(() => hasAlumniChildrenCache());
  const [showRotateOverlay, setShowRotateOverlay] = useState(false); // 🆕 overlay-state
  const restoredScrollRef = useRef(false);
  const filterPopoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchChildren = async () => {
      setError(null);
      try {
        if (!keycloak?.token) {
          setError(mapHttpErrorToMessage({ response: { status: 401 } }, t) as MappedError);
          setLoading(false);
          return;
        }
        const data = await loadChildrenOverview();
        if (!cancelled) setChildren(data);
      } catch (err) {
        if (!cancelled && !hasChildrenCache()) setError(mapHttpErrorToMessage(err, t) as MappedError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (keycloak?.token) fetchChildren();
    return () => {
      cancelled = true;
    };
  }, [keycloak?.token, t]);

  useEffect(() => {
    if (!showAlumni || !keycloak?.token) return undefined;

    let cancelled = false;
    const fetchAlumni = async () => {
      if (!hasAlumniChildrenCache()) setAlumniLoading(true);
      setAlumniError(null);
      try {
        const data = await loadAlumniChildrenOverview();
        if (!cancelled) setAlumni(data);
      } catch (err) {
        if (!cancelled && !hasAlumniChildrenCache()) setAlumniError(mapHttpErrorToMessage(err, t) as MappedError);
      } finally {
        if (!cancelled) {
          setAlumniFetched(true);
          setAlumniLoading(false);
        }
      }
    };

    fetchAlumni();
    return () => {
      cancelled = true;
    };
  }, [showAlumni, keycloak?.token, t]);

  useEffect(() => {
    if (!loading && !alumniLoading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading, alumniLoading]);

  useEffect(() => {
    setChildrenUiState({
      search,
      onlyMultiFamilies,
      onlyWithoutFamily,
      showAlumni,
      showDeletionRequested,
    });
  }, [search, onlyMultiFamilies, onlyWithoutFamily, showAlumni, showDeletionRequested]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!restoredScrollRef.current && (hasChildrenCache() || hasAlumniChildrenCache())) {
      restoredScrollRef.current = true;
      window.requestAnimationFrame(() => window.scrollTo({ top: getChildrenUiState().scrollY }));
    }

    let ticking = false;
    const persistScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setChildrenUiState({ scrollY: window.scrollY });
        ticking = false;
      });
    };
    window.addEventListener("scroll", persistScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", persistScroll);
      setChildrenUiState({ scrollY: window.scrollY });
    };
  }, []);

  // 🆕: check viewport + orientation
  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateOverlay = () => {
      const width = window.innerWidth;
      const isMobileLayout = width < 1024; // zelfde breakpoint als Tailwind lg
      const isPortrait = window.matchMedia?.("(orientation: portrait)")?.matches ?? false;

      // Alleen overlay tonen als:
      // - mobiele layout (sidebar ingeklapt / hamburger)
      // - én portrait
      setShowRotateOverlay(isMobileLayout && isPortrait);
    };

    updateOverlay();

    window.addEventListener("resize", updateOverlay);
    window.addEventListener("orientationchange", updateOverlay);

    return () => {
      window.removeEventListener("resize", updateOverlay);
      window.removeEventListener("orientationchange", updateOverlay);
    };
  }, []);

  useEffect(() => {
    if (!filterOpen || typeof document === "undefined") return undefined;

    const closeOnOutsideInteraction = (event: globalThis.MouseEvent | FocusEvent) => {
      const target = event.target;
      if (target instanceof Node && filterPopoverRef.current?.contains(target)) return;
      setFilterOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setFilterOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("focusin", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("focusin", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [filterOpen]);

  const handleAddChild = (): void => {
    void navigate("/family/children/new");
  };
  const handleViewChild = (id: Identifier): void => {
    void navigate(`/family/children/${id}/details`);
  };

  const formatDate = (value: string | Date | null | undefined): string => {
    const date = parseToDate(value);
    return date ? date.toLocaleDateString("nl-NL") : "-";
  };

  const getGenderIcon = (gender?: string): ReactElement => {
    switch (gender) {
      case "M":
        return <FaMars className="text-blue-500 text-xl" />;
      case "F":
        return <FaVenus className="text-pink-500 text-xl" />;
      default:
        return <FaGenderless className="text-gray-500 text-xl" />;
    }
  };

  const isWithdrawn = (c: ChildRecord): boolean => Boolean(c?.withdrawnAt) || String(c?.status || "").toLowerCase() === "withdrawn";

  const activeAdvancedFilterCount = useMemo(() => {
    let count = 0;
    if (onlyMultiFamilies) count += 1;
    if (onlyWithoutFamily) count += 1;
    if (showAlumni) count += 1;
    if (showDeletionRequested) count += 1;
    return count;
  }, [onlyMultiFamilies, onlyWithoutFamily, showAlumni, showDeletionRequested]);

  const filteredChildren = useMemo(() => {
    const source = showDeletionRequested
      ? children.filter((c) => Boolean(c.deletionRequestedAt))
      : showAlumni
        ? alumni.filter((c) => isWithdrawn(c) && !c.deletionRequestedAt)
        : children.filter((c) => !c.deceasedAt && !isWithdrawn(c) && !c.deletionRequestedAt);
    const term = (search || "").toLowerCase();
    return source
      .filter((c) => `${c.firstName} ${c.lastName}`.toLowerCase().includes(term))
      .filter((c) => (onlyMultiFamilies ? getChildFamilyCount(c) >= 2 : true))
      .filter((c) => (onlyWithoutFamily ? getChildFamilyCount(c) === 0 : true));
  }, [alumni, children, showAlumni, showDeletionRequested, search, onlyMultiFamilies, onlyWithoutFamily]);

  const clearFilters = () => {
    setOnlyMultiFamilies(false);
    setOnlyWithoutFamily(false);
    setShowAlumni(false);
    setShowDeletionRequested(false);
  };

  const currentError = showAlumni ? alumniError : error;
  if (currentError)
    return (
      <ErrorPage
        message={currentError.message}
        status={currentError.status}
        onRetry={
          showAlumni
            ? () => {
                setAlumniError(null);
                setAlumniFetched(false);
              }
            : () => window.location.reload()
        }
      />
    );

  const isLoading = showAlumni ? !alumniFetched || alumniLoading : loading;
  if (isLoading) return showInitialSpinner ? <Spinner /> : null;

  return (
    <PageTemplate
      title={null}
      wrapperClassName="max-w-none"
    >
      {/* 🔥 Zwarte overlay voor mobile portrait */}
      {showRotateOverlay && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-slate-950/90 px-6 text-center text-white backdrop-blur">
          <p className="mb-2 text-lg font-bold">
            {t("children.overview.rotate_title", {
              defaultValue: "Rotate your screen",
            })}
          </p>
          <p className="max-w-xs text-sm text-slate-200">
            {t("children.overview.rotate_message", {
              defaultValue:
                "This page works better in landscape mode or on a larger screen.",
            })}
          </p>
        </div>
      )}

      <div className="mx-auto max-w-7xl rounded-[32px] border border-slate-200/80 bg-white/95 p-5 shadow-[0_26px_70px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-slate-950/85 sm:p-6">
        <SlidePageTransition className="space-y-5">
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
                <FaChild aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("children.overview.section_label", { defaultValue: "Family administration" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("children.overview.title", { defaultValue: "Child management" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("children.overview.subtitle", {
                    defaultValue: "Manage child records, registrations and family links.",
                  })}
                </p>
              </div>
            </div>

            {(canCreateChildren || canManageFamilies) ? (
              <div className="ml-auto shrink-0 lg:mr-36">
                <button
                  type="button"
                  onClick={handleAddChild}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200 dark:hover:bg-indigo-400/20"
                >
                  <FaPlus aria-hidden="true" />
                  {t("children.overview.add_button", { defaultValue: "Add child" })}
                </button>
              </div>
            ) : null}
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaChild className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
          </div>
        </section>

        <section className="sticky top-3 z-50 rounded-[24px] border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-slate-900/95">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <label className="relative flex-1">
              <span className="sr-only">{t("children.overview.search_placeholder")}</span>
              <FaSearch aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("children.overview.search_placeholder")}
                aria-label={t("children.overview.search_placeholder")}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-400/10"
              />
            </label>

            <div ref={filterPopoverRef} className="relative">
              <button
                type="button"
                onClick={() => setFilterOpen((open) => !open)}
                className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:w-auto ${
                  filterOpen || activeAdvancedFilterCount > 0
                    ? "border-indigo-300 bg-indigo-600 text-white shadow-lg shadow-indigo-900/15 hover:bg-indigo-700 dark:border-indigo-400 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-400"
                    : "border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:text-indigo-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                } ${activeAdvancedFilterCount > 0 ? "pr-10" : ""}`}
                aria-expanded={filterOpen}
              >
                <span className="inline-flex h-full items-center justify-center gap-2 px-5">
                  <FaFilter aria-hidden="true" />
                  {t("common.filter", { defaultValue: "Filter" })}
                </span>
                {activeAdvancedFilterCount > 0 && (
                  <span className="mr-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs text-white">
                    {activeAdvancedFilterCount}
                  </span>
                )}
              </button>
              {activeAdvancedFilterCount > 0 && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    clearFilters();
                    setFilterOpen(false);
                  }}
                  className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-white transition hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/70"
                  aria-label={t("common.clear", { defaultValue: "Clear" })}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}

              {filterOpen && (
                <div className="absolute right-0 z-[70] mt-3 w-[min(360px,calc(100vw-2rem))] rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_24px_60px_rgba(15,23,42,0.18)] dark:border-white/10 dark:bg-slate-900">
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-indigo-600"
                        checked={onlyMultiFamilies}
                        onChange={(e) => {
                          setOnlyMultiFamilies(e.target.checked);
                          if (e.target.checked) setOnlyWithoutFamily(false);
                        }}
                      />
                      {t("children.overview.only_multi_families", { defaultValue: "Only children linked to 2+ families" })}
                    </label>

                    <label className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50/70 px-4 py-3 text-sm font-bold text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-red-600"
                        checked={onlyWithoutFamily}
                        onChange={(e) => {
                          setOnlyWithoutFamily(e.target.checked);
                          if (e.target.checked) setOnlyMultiFamilies(false);
                        }}
                      />
                      {t("children.overview.only_without_family", { defaultValue: "Only children without a family" })}
                    </label>

                    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-rose-600"
                        checked={showAlumni}
                        onChange={(e) => {
                          setShowAlumni(e.target.checked);
                          if (e.target.checked) setShowDeletionRequested(false);
                        }}
                      />
                      {t("children.overview.show_alumni", { defaultValue: "Show alumni" })}
                    </label>

                    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-sm font-bold text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-amber-600"
                        checked={showDeletionRequested}
                        onChange={(e) => {
                          setShowDeletionRequested(e.target.checked);
                          if (e.target.checked) setShowAlumni(false);
                        }}
                      />
                      {t("children.overview.show_deletion_requested", { defaultValue: "Show pending deletion" })}
                    </label>
                  </div>

                  <div className="mt-4 flex justify-between gap-2">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="rounded-full px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                    >
                      {t("common.clear", { defaultValue: "Clear" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterOpen(false)}
                      className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    >
                      {t("common.close", { defaultValue: "Close" })}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-col gap-2 border-b border-slate-200 px-5 py-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("children.overview.list_label", { defaultValue: "Children" })}
              </p>
              <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                {t("children.overview.list_title", { defaultValue: "Child overview" })}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral">
                {t("common.result_count", {
                  count: filteredChildren.length,
                  defaultValue: "{{count}} results",
                })}
              </Badge>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {showDeletionRequested
                  ? t("children.overview.deletion_requested_mode", { defaultValue: "Showing children pending deletion" })
                  : showAlumni
                  ? t("children.overview.alumni_mode", { defaultValue: "Showing withdrawn children" })
                  : t("children.overview.active_mode", { defaultValue: "Showing active children" })}
              </span>
            </div>
          </div>

          {filteredChildren.length > 0 ? (
            <div className="divide-y divide-slate-200/80 dark:divide-white/10">
              {filteredChildren.map((child) => {
                const familyCount = getChildFamilyCount(child);
                const isMulti = familyCount >= 2;
                const hasNoFamily = familyCount === 0;
                const withdrawn = isWithdrawn(child);
                const childName = [child.firstName, child.lastName].filter(Boolean).join(" ").trim();

                return (
                  <article
                    key={child.id}
                    className="group grid cursor-pointer gap-4 px-5 py-4 transition hover:bg-slate-50/80 dark:hover:bg-white/[0.035] lg:grid-cols-[minmax(240px,1.05fr)_minmax(210px,0.85fr)_170px_170px_auto] lg:items-center"
                    onClick={() => handleViewChild(child.id)}
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-indigo-100 bg-indigo-50 text-sm font-bold text-indigo-700 shadow-sm dark:border-indigo-400/20 dark:bg-indigo-400/10 dark:text-indigo-200">
                        {initialsForChild(child)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-base font-bold text-slate-950 dark:text-white">{childName || "—"}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center text-xs text-slate-500 dark:text-slate-400">
                            {getGenderIcon(child.gender)}
                          </span>
                          <CommittedValidate committed={child.committed} />
                          <WaitingValidate waiting={child.waiting} />
                          {child.deletionRequestedAt ? (
                            <Badge tone="warn">
                              {t("children.overview.deletion_requested", { defaultValue: "Pending deletion" })}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("children.overview.family_links", { defaultValue: "Families" })}
                      </p>
                      <Badge tone={hasNoFamily ? "danger" : isMulti ? "warn" : "neutral"}>
                        <span className="inline-flex items-center gap-1">
                          {hasNoFamily || isMulti ? <FaExclamationTriangle aria-hidden="true" /> : <FaUsers aria-hidden="true" />}
                          {hasNoFamily
                            ? t("children.overview.no_family_badge", { defaultValue: "No family" })
                            : t("children.overview.multi_family_badge", {
                                defaultValue: "{{count}} families",
                                count: familyCount,
                              })}
                        </span>
                      </Badge>
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("children.table.registration_date")}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <FaCalendarAlt aria-hidden="true" className="text-sky-500" />
                        {formatDate(child.dateRegistration)}
                      </span>
                    </div>

                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400 lg:hidden">
                        {t("children.table.birth_date")}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <FaBirthdayCake aria-hidden="true" className="text-violet-500" />
                        {formatDate(child.dateBirth)} <EasterTimingIndicator dateBirth={child.dateBirth} />
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      {withdrawn && <Badge tone="danger">{t("children.details.withdrawn", { defaultValue: "Withdrawn" })}</Badge>}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewChild(child.id);
                        }}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 text-xs font-bold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-400 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200"
                      >
                        {t("common.actions.view", { defaultValue: "View" })}
                        <FaArrowRight aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-slate-100 text-2xl text-slate-400 dark:bg-white/5 dark:text-slate-500">
                <FaChild aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-lg font-bold text-slate-950 dark:text-white">
                {t("children.overview.none_available", { defaultValue: "No children found" })}
              </h3>
            </div>
          )}
        </section>
        </SlidePageTransition>
      </div>
    </PageTemplate>
  );
};

export default ChildrenOverview;
